import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Lock, RefreshCw, Receipt, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

function lastMonths(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i += 1) {
    out.push(monthKey(d));
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const STATUS_LABEL = { provisional: "Provisoire (mois en cours)", computed: "Calculé à la volée", frozen: "Figé" };

/** Facturation SaaS : patients actifs ≥ 1 jour dans le mois, par clinique. */
export default function BillingTab() {
  const months = useMemo(() => lastMonths(12), []);
  const [month, setMonth] = useState(months[0]);
  const [data, setData] = useState({ items: [], totalActivePatients: 0 });
  const [loading, setLoading] = useState(true);
  const [freezing, setFreezing] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/billing/active-patients", { params: { month } });
      setData(res.data);
    } catch {
      toast.error("Impossible de charger la facturation");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const isCurrent = month === months[0];
  const allFrozen = data.items.length > 0 && data.items.every((i) => i.status === "frozen");

  const downloadCsv = async (clinicId) => {
    try {
      const res = await api.get("/billing/active-patients.csv", { params: { month, ...(clinicId ? { clinicId } : {}) }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hana-facturation-${month}${clinicId ? `-${clinicId}` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export CSV impossible");
    }
  };

  const freeze = async () => {
    if (!window.confirm(`Figer le mois ${month} ? Les chiffres ne bougeront plus, même si des patients sont désactivés ensuite.`)) return;
    setFreezing(true);
    try {
      const res = await api.post(`/billing/snapshots/${month}/freeze`);
      toast.success(res.data.frozen ? `${res.data.frozen} clinique(s) figée(s)` : "Mois déjà figé");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gel impossible");
    } finally {
      setFreezing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="billing-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center gap-3">
        <Receipt className="w-5 h-5 text-amber-600" />
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-semibold text-slate-800">Facturation — patients actifs</h2>
          <p className="text-xs text-slate-500">Règle : un patient est compté s'il a été actif au moins un jour dans le mois. Les mois échus sont figés automatiquement le 1er à 00h10.</p>
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="billing-month-select">
          {months.map((m) => <option key={m} value={m}>{m}{m === months[0] ? " (en cours)" : ""}</option>)}
        </select>
        <button onClick={load} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50" title="Actualiser" data-testid="billing-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
        <button onClick={() => downloadCsv()} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50" data-testid="billing-export-all-btn">
          <Download className="w-4 h-4" /> CSV
        </button>
        {!isCurrent && !allFrozen && (
          <button onClick={freeze} disabled={freezing} className="flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50" data-testid="billing-freeze-btn">
            <Lock className="w-4 h-4" /> Figer ce mois
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">{month} · {data.items.length} clinique(s)</span>
          <span className="font-bold text-slate-800" data-testid="billing-total">{data.totalActivePatients} patient(s) facturable(s)</span>
        </div>
        {loading ? (
          <p className="p-6 text-sm text-slate-400">Chargement…</p>
        ) : data.items.length === 0 ? (
          <p className="p-6 text-sm text-slate-400" data-testid="billing-empty">Aucun patient actif sur ce mois</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 bg-white">
              <tr>
                <th className="px-5 py-2">Clinique</th>
                <th className="px-3 py-2 text-right">Actifs</th>
                <th className="px-3 py-2 text-right">Entrées</th>
                <th className="px-3 py-2 text-right">Sorties</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <BillingRow key={row.clinicId} row={row} expanded={expanded === row.clinicId} onToggle={() => setExpanded(expanded === row.clinicId ? null : row.clinicId)} onCsv={() => downloadCsv(row.clinicId)} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BillingRow({ row, expanded, onToggle, onCsv }) {
  return (
    <>
      <tr className="border-t border-slate-100" data-testid={`billing-row-${row.clinicId}`}>
        <td className="px-5 py-3 font-medium text-slate-800">{row.clinicName}</td>
        <td className="px-3 py-3 text-right font-bold" data-testid={`billing-count-${row.clinicId}`}>{row.activePatientsCount}</td>
        <td className="px-3 py-3 text-right text-emerald-700">+{row.activations}</td>
        <td className="px-3 py-3 text-right text-rose-700">−{row.deactivations}</td>
        <td className="px-3 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-full ${row.status === "frozen" ? "bg-slate-800 text-white" : row.status === "provisional" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
            {STATUS_LABEL[row.status] || row.status}
          </span>
        </td>
        <td className="px-3 py-3 text-right whitespace-nowrap">
          <button onClick={onCsv} className="p-1.5 text-slate-500 hover:text-slate-800" title="Export CSV clinique" data-testid={`billing-csv-${row.clinicId}`}><Download className="w-4 h-4" /></button>
          <button onClick={onToggle} className="p-1.5 text-slate-500 hover:text-slate-800" data-testid={`billing-toggle-${row.clinicId}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={6} className="px-5 py-3">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr><th className="text-left py-1">Identifiant HANA</th><th className="text-left py-1">Patient</th><th className="text-left py-1">Actif du</th><th className="text-left py-1">Actif au</th><th className="text-right py-1">Jours</th><th className="text-left py-1 pl-3">Aujourd'hui</th></tr>
              </thead>
              <tbody>
                {row.patients.map((p) => (
                  <tr key={p.patientId} className="border-t border-slate-200" data-testid={`billing-patient-${p.patientId}`}>
                    <td className="py-1 font-mono">{p.hanaId}</td>
                    <td className="py-1">{p.name}</td>
                    <td className="py-1">{p.activeFrom}</td>
                    <td className="py-1">{p.activeTo || "fin de mois"}</td>
                    <td className="py-1 text-right">{p.daysActive}</td>
                    <td className="py-1 pl-3">{p.currentlyActive ? "actif" : "inactif"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
